import { CircleAlertIcon, Loader2 } from 'lucide-react'
import { type ReactNode } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import { Banner, CollapsibleContainer, SolidCircleCheckIcon } from 'slab'

interface CollapsibleDataContainerProps {
  heading: ReactNode
  isLoading: boolean
  error?: boolean | string | null
  localStorageKey: string
  defaultOpen?: boolean
  loadingContent?: ReactNode
  errorContent?: ReactNode
  successContent: ReactNode
  loadingIcon?: ReactNode
}

export const CollapsibleDataContainer = ({
  heading,
  isLoading,
  error,
  localStorageKey,
  defaultOpen = true,
  loadingContent,
  errorContent,
  successContent,
}: CollapsibleDataContainerProps) => {
  const [isOpenByDefault, setIsOpenByDefault] = useLocalStorage(
    localStorageKey,
    defaultOpen
  )

  const handleOpenChange = (open: boolean) => {
    setIsOpenByDefault(open)
  }

  const getHeadingIcon = () => {
    if (isLoading) {
      return <Loader2 data-testid='loading-icon' className='animate-spin' size={20} />
    }

    if (error) {
      return (
        <div className='text-red-400' data-testid='error-icon'>
          <CircleAlertIcon aria-label='(error)' size={20} />
        </div>
      )
    }

    return (
      <div className='text-green-400'>
        <SolidCircleCheckIcon aria-label='(complete)' size={20} />
      </div>
    )
  }

  if (isLoading) {
    return (
      <CollapsibleContainer
        heading={heading}
        defaultOpen={isOpenByDefault}
        onOpenChange={handleOpenChange}
        headingIconContent={getHeadingIcon()}
      >
        {loadingContent || (
          <div className='p-4 text-center text-gray-500'>
            Loading...
          </div>
        )}
      </CollapsibleContainer>
    )
  }

  if (error) {
    return (
      <CollapsibleContainer
        heading={heading}
        defaultOpen={isOpenByDefault}
        onOpenChange={handleOpenChange}
        headingIconContent={getHeadingIcon()}
      >
        {errorContent || (
          <Banner type='critical' title='Error loading data'>
            {typeof error === 'string' ? error : 'Error loading data'}
          </Banner>
        )}
      </CollapsibleContainer>
    )
  }

  return (
    <CollapsibleContainer
      heading={heading}
      defaultOpen={isOpenByDefault}
      onOpenChange={handleOpenChange}
      headingIconContent={getHeadingIcon()}
    >
      {successContent}
    </CollapsibleContainer>
  )
}
